import { Box } from "@twilio-paste/core/box";
import { Stack } from "@twilio-paste/core/stack";
import { Text } from "@twilio-paste/core/text";
import { useTheme } from "@twilio-paste/theme";
import { graphql, useStaticQuery, withPrefix } from "gatsby";
import { SitesQuery } from "queries";
import React from "react";
import { v4 as uuid } from "uuid";
import { Code } from "..";
import { addPrefixAndUpdateInternalLinks, getLastValidElementFromPath } from "../../../helper";
import { Property as PropertyType } from "../../../types";
import { AnchorContainer } from "../AnchorContainer";
import { AnchoredHeading } from "../AnchoredHeading";

export type PropertyProps = PropertyType & {
    isRootProp?: boolean;
    codeBlock?: boolean;
    nested?: PropertyProps[];
};
export const queryFragment = graphql`
    fragment SitePath on SitePage {
        path
    }
`;
export const query = graphql`
    {
        pages: allSitePage {
            nodes {
                ...SitePath
            }
        }
    }
`;

const Property: React.FC<Partial<PropertyProps>> = ({
    name,
    optional,
    type,
    description,
    isRootProp = false,
    codeBlock = true,
    nested,
    defaultvalue
}) => {
    let typePath: string | undefined;
    let types: string | undefined;
    const { pages }: SitesQuery = useStaticQuery(query);
    const {
        space: { space30 }
    } = useTheme();
    description = description && addPrefixAndUpdateInternalLinks(description, pages.nodes);
    if (type) {
        const extracted = type.names[0].match(/(?<=<)(.*)(?=>)/g) || type.names;

        if (type.names.length === 1 && extracted?.length) {
            typePath = pages.nodes.filter(
                ({ path }: { path: string }) => getLastValidElementFromPath(path) === extracted[0]
            )[0]?.path;
        }

        types = type.names.join(" | ");
    }

    const details = (
        <>
            {name}
            {optional ? "?" : ""}
            {!!name && !!type && ": "}
            {typePath ? <a href={withPrefix(typePath)}>{types}</a> : <span>{types}</span>}
        </>
    );

    const generateNested = (props: PropertyProps[]) => (
        <ul style={{ listStyle: "circle", marginTop: space30 }}>
            {props.map((prop: PropertyProps) => (
                <>
                    <Property isRootProp={false} {...prop} />
                    {!!prop.nested && generateNested(prop.nested)}
                </>
            ))}
        </ul>
    );
    const renderDefaultValue = (defaultValue: string) => (
        <Stack orientation="horizontal" spacing="space30">
            <Text as="p" marginY="space0" fontWeight="fontWeightBold">
                Default Value:
            </Text>
            <Text as="p" marginY="space0">
                {defaultValue}
            </Text>
        </Stack>
    );
    const renderAsProp = () => (
        <Box key={uuid()} marginBottom={isRootProp ? "space90" : "space30"} as="li">
            <Code bold={{ bold: isRootProp }} as="code">
                {details}
            </Code>
            {description ? (
                <>
                    {!isRootProp && ` - `}
                    <Text
                        as={isRootProp ? "p" : "span"}
                        marginY={isRootProp ? "space30" : "space0"}
                        dangerouslySetInnerHTML={{ __html: description.replace(/^(?:<p>)?(.*?)(?:<\/p>)?$/, "$1") }}
                    />
                </>
            ) : null}
            {!!defaultvalue && renderDefaultValue(defaultvalue)}
            {!!nested && isRootProp && generateNested(nested)}
        </Box>
    );

    const renderAsHeader = () => (
        <AnchorContainer paddingBottom="space60" key={uuid()} id={name}>
            <AnchoredHeading as="h3" variant="heading30" marginBottom="space0" id={name}>
                {details}
            </AnchoredHeading>
            {/* eslint-disable-next-line react/no-danger */}
            <div dangerouslySetInnerHTML={{ __html: description! }} />
            {!!defaultvalue && renderDefaultValue(defaultvalue)}
        </AnchorContainer>
    );

    return <>{codeBlock ? renderAsProp() : renderAsHeader()}</>;
};

export { Property };
